import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./AdminSettingsPage.css";

const AdminSettingsPage = () => {
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmNext, setConfirmNext] = useState(null);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    const res = await AdminService.getAdminWithdrawalStatus();
    setLoading(false);
    if (res?.success && res.data && typeof res.data.withdrawal_status === "boolean") {
      setEnabled(res.data.withdrawal_status);
    } else if (res?.success === false) {
      toast.error(res?.message || "Failed to fetch withdrawal status.");
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const applyStatus = async (nextValue) => {
    setSaving(true);
    try {
      const res = await AdminService.setAdminWithdrawalStatus(nextValue);
      if (res?.success) {
        const value =
          typeof res.data?.withdrawal_status === "boolean"
            ? res.data.withdrawal_status
            : nextValue;
        setEnabled(value);
        toast.success(
          res.message || (value ? "Withdrawals are now active" : "Withdrawals are now off")
        );
      } else {
        toast.error(res?.message || "Failed to update withdrawal status.");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to update withdrawal status.");
    } finally {
      setSaving(false);
      setConfirmNext(null);
    }
  };

  const requestToggle = () => {
    if (saving || loading) return;
    setConfirmNext(!enabled);
  };

  return (
    <main
      className={`dashboard-main dashboard-subpage payout-ctrl-page ${
        !loading && !enabled ? "payout-ctrl-page--off" : ""
      }`}
    >
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Payout &amp; Withdrawal</span>
          </p>
          <h1 className="dashboard-hero__title">Payout &amp; Withdrawal</h1>
          <p className="dashboard-hero__subtitle">
            One global switch to turn user withdrawals on or off
          </p>
        </div>
        <div className="payout-ctrl-hero-status" aria-live="polite">
          {loading ? (
            <span className="payout-ctrl-hero-pill payout-ctrl-hero-pill--muted">
              <i className="fas fa-spinner fa-spin" /> Checking…
            </span>
          ) : (
            <span
              className={`payout-ctrl-hero-pill ${
                enabled ? "payout-ctrl-hero-pill--on" : "payout-ctrl-hero-pill--off"
              }`}
            >
              <span className="payout-ctrl-hero-dot" />
              {enabled ? "Withdrawals ON" : "Withdrawals OFF"}
            </span>
          )}
        </div>
      </section>

      <div className="dashboard-body">
        {loading ? (
          <div className="payout-ctrl-loading">
            <div className="payout-ctrl-loading__ring" aria-hidden />
            <p>Loading payout &amp; withdrawal status…</p>
          </div>
        ) : (
          <section className={`payout-ctrl-panel ${enabled ? "payout-ctrl-panel--on" : "payout-ctrl-panel--off"}`}>
            <div className="payout-ctrl-panel__main">
              <div className={`payout-ctrl-orb ${enabled ? "payout-ctrl-orb--on" : "payout-ctrl-orb--off"}`}>
                <i className={`fas ${enabled ? "fa-check" : "fa-ban"}`} />
              </div>
              <div className="payout-ctrl-copy">
                <p className="payout-ctrl-kicker">Global user control</p>
                <h2>{enabled ? "Withdrawals (Payout) are live" : "Withdrawals (Payout) are off"}</h2>
                <p>
                  {enabled
                    ? "Users can submit new withdrawal requests. Turn this off to freeze payouts instantly."
                    : "New user withdrawal requests are blocked. They will see “Withdrawals are currently inactive”."}
                </p>
              </div>
              <div className="payout-ctrl-switch-wrap">
                <span className="payout-ctrl-switch-label">{enabled ? "On" : "Off"}</span>
                <label
                  className="payout-ctrl-toggle"
                  title={enabled ? "Turn withdrawals off" : "Turn withdrawals on"}
                >
                  <input
                    type="checkbox"
                    checked={enabled}
                    disabled={saving}
                    onChange={requestToggle}
                  />
                  <span className="payout-ctrl-toggle__track">
                    <span className="payout-ctrl-toggle__thumb" />
                  </span>
                </label>
                <button
                  type="button"
                  className={`payout-ctrl-action ${enabled ? "payout-ctrl-action--off" : "payout-ctrl-action--on"}`}
                  onClick={requestToggle}
                  disabled={saving}
                >
                  {enabled ? "Turn withdrawals off" : "Turn withdrawals on"}
                </button>
              </div>
            </div>

            <div className="payout-ctrl-impacts">
              <article>
                <i className="fas fa-user-check" />
                <div>
                  <strong>User requests</strong>
                  <span>{enabled ? "Create-withdrawal is allowed" : "Create-withdrawal is rejected"}</span>
                </div>
              </article>
              <article>
                <i className="fas fa-globe" />
                <div>
                  <strong>Scope</strong>
                  <span>Applies to every user on the platform</span>
                </div>
              </article>
              <article>
                <i className="fas fa-bolt" />
                <div>
                  <strong>Effect</strong>
                  <span>Takes effect as soon as you confirm</span>
                </div>
              </article>
            </div>

            <footer className="payout-ctrl-panel__foot">
              <p>
                Use this when you need an emergency payout freeze. Staking ROI pause is still per-user
                from User Stakes.
              </p>
              <button
                type="button"
                className="payout-ctrl-refresh"
                onClick={fetchStatus}
                disabled={saving}
              >
                <i className="fas fa-sync-alt" /> Refresh status
              </button>
            </footer>
          </section>
        )}
      </div>

      {confirmNext != null && (
        <div
          className="payout-ctrl-overlay"
          onClick={() => !saving && setConfirmNext(null)}
          role="presentation"
        >
          <div
            className={`payout-ctrl-modal ${confirmNext ? "payout-ctrl-modal--on" : "payout-ctrl-modal--off"}`}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="payout-ctrl-modal-title"
          >
            <div className="payout-ctrl-modal__icon" aria-hidden>
              <i className={`fas ${confirmNext ? "fa-unlock" : "fa-lock"}`} />
            </div>
            <h3 id="payout-ctrl-modal-title">
              {confirmNext ? "Turn withdrawals on?" : "Turn withdrawals off?"}
            </h3>
            <p>
              {confirmNext
                ? "Users will be able to create withdrawal requests again."
                : "All new user withdrawal requests will be rejected until you turn this back on."}
            </p>
            <div className="payout-ctrl-modal__actions">
              <button
                type="button"
                className="payout-ctrl-cancel"
                onClick={() => setConfirmNext(null)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className={confirmNext ? "payout-ctrl-confirm-on" : "payout-ctrl-confirm-off"}
                onClick={() => applyStatus(confirmNext)}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <i className="fas fa-spinner fa-spin" /> Saving…
                  </>
                ) : confirmNext ? (
                  "Turn on"
                ) : (
                  "Turn off"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default AdminSettingsPage;
